import { useMemo, useRef, useState } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";
import { SxProps, Theme } from "@mui/material/styles";

export type FilterableSelectOption = {
    value: number | string;
    label: string;
};

const DEFAULT_ALL_VALUE = "";

type FilterableSelectProps = {
    label: string;
    value?: number | string;
    options: FilterableSelectOption[];
    onChange: (value: number | string | undefined) => void;
    size?: "small" | "medium";
    sx?: SxProps<Theme>;
    includeAll?: boolean;
    includeNone?: boolean;
    allOptionLabel?: string;
    allOptionValue?: number | string;
    minWidth?: number;
    fullWidth?: boolean;
    required?: boolean;
    searchPlaceholder?: string;
};

export default function FilterableSelect({
    label,
    value,
    options,
    onChange,
    size = "small",
    sx,
    includeAll = true,
    includeNone = false,
    allOptionLabel = "All",
    allOptionValue = DEFAULT_ALL_VALUE,
    minWidth = 200,
    fullWidth = false,
    required = false,
    searchPlaceholder = "Search...",
}: FilterableSelectProps) {
    const [filter, setFilter] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);

    const mergedOptions = useMemo(() => {
        const allOption: FilterableSelectOption = { value: allOptionValue, label: allOptionLabel };
        const noneOption: FilterableSelectOption = { value: 0, label: "None" };

        return [
            ...(includeAll ? [allOption] : []),
            ...(includeNone ? [noneOption] : []),
            ...options,
        ];
    }, [allOptionLabel, allOptionValue, includeAll, includeNone, options]);

    const filteredOptions = useMemo(() => {
        const query = filter.trim().toLowerCase();
        if (!query) {
            return mergedOptions;
        }

        return mergedOptions.filter((option) =>
            option.label.toLowerCase().includes(query),
        );
    }, [filter, mergedOptions]);

    const selectValue =
        value === undefined
            ? includeAll
                ? allOptionValue
                : ""
            : value;

    const selectedLabel =
        mergedOptions.find((option) => option.value === selectValue)?.label ?? "";

    const handleChange = (event: SelectChangeEvent<number | string>) => {
        const newValue = event.target.value;
        if (includeAll && newValue === allOptionValue) {
            onChange(allOptionValue === DEFAULT_ALL_VALUE ? undefined : allOptionValue);
            return;
        }

        onChange(newValue);
    };

    return (
        <FormControl
            size={size}
            required={required}
            fullWidth={fullWidth}
            sx={{
                minWidth: fullWidth ? undefined : minWidth,
                ...sx,
            }}
        >
            <InputLabel id={`${label}-filterable-select-label`}>{label}</InputLabel>
            <Select
                labelId={`${label}-filterable-select-label`}
                label={label}
                value={selectValue}
                onChange={handleChange}
                onOpen={() => {
                    setTimeout(() => searchInputRef.current?.focus(), 0);
                }}
                onClose={() => setFilter("")}
                MenuProps={{
                    autoFocus: false,
                    PaperProps: { style: { maxHeight: 320 } },
                }}
                renderValue={() => selectedLabel}
            >
                <ListSubheader sx={{ lineHeight: "unset", py: 1, px: 1 }}>
                    <TextField
                        inputRef={searchInputRef}
                        size="small"
                        placeholder={searchPlaceholder}
                        fullWidth
                        value={filter}
                        onChange={(event) => setFilter(event.target.value)}
                        onKeyDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    />
                </ListSubheader>
                {filteredOptions.length === 0 ? (
                    <MenuItem disabled>No matches</MenuItem>
                ) : (
                    filteredOptions.map((option) => (
                        <MenuItem key={String(option.value)} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))
                )}
            </Select>
        </FormControl>
    );
}
